import React, { useState } from 'react'
import { X, AlertTriangle } from 'lucide-react'
import { useDeleteRestaurant } from '@/hooks/admin/useRestaurants'
import type { RestaurantListDTO } from '@/types/admin'

interface DeleteRestaurantModalProps {
  restaurant: RestaurantListDTO
  onClose: () => void
}

export function DeleteRestaurantModal({ restaurant, onClose }: DeleteRestaurantModalProps) {
  const [confirmText, setConfirmText] = useState('')
  const [error, setError] = useState('')

  const deleteMutation = useDeleteRestaurant()

  const handleDelete = async () => {
    if (confirmText !== restaurant.name) {
      setError('Il nome del ristorante non corrisponde')
      return
    }

    try {
      await deleteMutation.mutateAsync(restaurant.id)
      onClose()
    } catch (error) {
      console.error('Failed to delete restaurant:', error)
      setError('Errore durante l\'eliminazione del ristorante')
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 dark:bg-gray-900 bg-opacity-75 dark:bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
              <h3 className="text-lg font-semibold text-red-900 dark:text-red-200">
                Elimina Ristorante
              </h3>
            </div>
            <button
              onClick={onClose}
              className="text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-4 space-y-4">
            {/* Warning */}
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-sm font-semibold text-red-800 dark:text-red-300 mb-2">
                ⚠️ ATTENZIONE: Questa azione è irreversibile!
              </p>
              <ul className="text-sm text-red-700 dark:text-red-400 space-y-1 list-disc list-inside">
                <li>Tutti i dati del ristorante saranno eliminati permanentemente</li>
                <li>Menu, prodotti, categorie e customizzazioni saranno persi</li>
                <li>Storico ordini e analitiche saranno cancellati</li>
                <li>Tutti gli utenti associati perderanno l'accesso</li>
              </ul>
            </div>

            {/* Restaurant Info */}
            <div className="bg-gray-50 dark:bg-gray-900/50 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                Ristorante da eliminare:
              </p>
              <p className="font-semibold text-gray-900 dark:text-white">{restaurant.name}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400">{restaurant.email}</p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Piano: <span className="font-medium">{restaurant.currentPlan}</span>
              </p>
            </div>

            {/* Confirmation Input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Per confermare, digita esattamente il nome del ristorante:
              </label>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={restaurant.name}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Il nome è case-sensitive e deve corrispondere esattamente
              </p>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900/50 border-t border-gray-200 dark:border-gray-700 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleteMutation.isPending || confirmText !== restaurant.name}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {deleteMutation.isPending ? 'Eliminazione...' : 'Elimina Definitivamente'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
